import * as THREE from 'three';
import Window from '../models/window.js';

class WindowController {
  constructor(sceneManager, wallController) {
    this.scene = sceneManager.getScene();
    this.wallController = wallController;
    this.windows = [];
    
    // Stato posizionamento
    this.placementMode = false;
    this.selectedWall = null;
    this.previewWindow = null;
    this.previewMesh = null;

    // Cache dell'ultima posizione calcolata dal raycast durante la preview:
    // viene riusata da placeWindow per garantire che il piazzamento coincida
    // esattamente con la preview (ground-plane e wall-raycast possono divergere)
    this._lastPreviewPositionOnWall = null;

    // Tipo e dimensioni della finestra in corso di posizionamento
    // (impostati da enablePlacementMode in base all'item del catalogo)
    this.currentWindowWidth  = 1.2;
    this.currentWindowHeight = 1.4;
    this.currentSillHeight   = 0.9;
    
    // Distanza minima tra aperture (40cm)
    this.minimumSpacing = 0.4;
    
    // Raycaster per rilevamento pareti
    this.raycaster = new THREE.Raycaster();

    // Callback opzionale: chiamata quando finestre vengono aggiunte/rimosse
    this.onOpeningsChange = null;

    // Etichetta misure live (distanze sinistra/destra durante il posizionamento)
    this.measurementLabel = null;
    this.setupMeasurementLabel();
  }

  // ETICHETTA MISURE LIVE
  setupMeasurementLabel() {
    this.measurementLabel = document.createElement('div');
    this.measurementLabel.id = 'window-measurement-label';
    Object.assign(this.measurementLabel.style, {
      position:      'fixed',
      background:    'rgba(10, 10, 30, 0.88)',
      color:         '#ffffff',
      padding:       '5px 14px',
      borderRadius:  '20px',
      fontSize:      '14px',
      fontWeight:    'bold',
      fontFamily:    'system-ui, sans-serif',
      letterSpacing: '0.03em',
      pointerEvents: 'none',
      display:       'none',
      zIndex:        '2000',
      whiteSpace:    'nowrap',
      border:        '1.5px solid rgba(0,204,0,0.75)',
      boxShadow:     '0 2px 10px rgba(0,0,0,0.45)',
      transform:     'translate(-50%, -300%)',
    });
    document.body.appendChild(this.measurementLabel);
  }

  /**
   * Calcola e mostra le distanze tra il bordo della finestra in preview e
   * le estremità della parete
   */
  updateMeasurementLabel(wall, positionOnWall, openingWidth, screenX, screenY, isValid, flipped = false) {
    if (!this.measurementLabel) return;

    const wallLength   = wall.getLength();
    const halfW        = openingWidth / 2 / wallLength;
    const openingStart = positionOnWall - halfW;
    const openingEnd   = positionOnWall + halfW;

    // Distanza dall'inizio e dalla fine della parete
    let leftDist  = Math.max(0, openingStart * wallLength);
    let rightDist = Math.max(0, (1 - openingEnd) * wallLength);

    // Se la camera guarda la parete dal lato opposto, sinistra e destra
    // risultano visivamente scambiate → inverto i valori
    if (flipped) [leftDist, rightDist] = [rightDist, leftDist];

    const fmt = d => d < 1 ? `${(d * 100).toFixed(0)} cm` : `${d.toFixed(2)} m`;

    this.measurementLabel.textContent  = `← ${fmt(leftDist)}  ·  ${fmt(rightDist)} →`;
    this.measurementLabel.style.left   = `${screenX}px`;
    this.measurementLabel.style.top    = `${screenY}px`;
    this.measurementLabel.style.display     = 'block';
    this.measurementLabel.style.borderColor = isValid
      ? 'rgba(0,204,0,0.75)'
      : 'rgba(255,60,60,0.85)';
  }

  hideMeasurementLabel() {
    if (this.measurementLabel) this.measurementLabel.style.display = 'none';
  }
  
  enablePlacementMode(catalogItem) {
    // Legge dimensioni e sillHeight dall'item del catalogo
    if (catalogItem) {
      this.currentWindowWidth  = catalogItem.dimensions?.width  ?? 1.2;
      this.currentWindowHeight = catalogItem.dimensions?.height ?? 1.4;
      this.currentSillHeight   = catalogItem.sillHeight         ?? 0.9;
    }

    this.placementMode = true;
    this.createPreview();
    console.log(`Modalità posizionamento finestre: ATTIVA (${this.currentWindowWidth}×${this.currentWindowHeight}, sill=${this.currentSillHeight})`);
  }
  
  disablePlacementMode() {
    this.placementMode = false;
    this.removePreview();
    this.selectedWall = null;
    this.hideMeasurementLabel();
    console.log('Modalità posizionamento finestre: DISATTIVATA');
  }
  
  togglePlacementMode() {
    if (this.placementMode) {
      this.disablePlacementMode();
    } else {
      this.enablePlacementMode();
    }
  }
  
  createPreview() {
    // Crea una finestra preview trasparente con le dimensioni correnti
    const previewMaterial = new THREE.MeshBasicMaterial({
      color: 0x00cc00,
      transparent: true,
      opacity: 0.5,
      wireframe: true
    });

    const geometry = new THREE.BoxGeometry(
      this.currentWindowWidth,
      this.currentWindowHeight,
      0.2
    );
    this.previewMesh = new THREE.Mesh(geometry, previewMaterial);
    this.previewMesh.visible = false;
    this.scene.add(this.previewMesh);
  }
  
  removePreview() {
    if (this.previewMesh) {
      this.scene.remove(this.previewMesh);
      this.previewMesh.geometry.dispose();
      this.previewMesh.material.dispose();
      this.previewMesh = null;
    }
  }
  
  updatePreview(point, mouseEvent, camera) {
    if (!this.placementMode || !this.previewMesh) return;
    
    // Trova la parete più vicina usando il raycasting diretto
    const wallResult = this.findNearestWallWithRaycast(point, mouseEvent, camera);
    
    if (wallResult.wall) {
      this.selectedWall = wallResult.wall;
      const wall = wallResult.wall;
      
      // Usa la posizione calcolata dal raycast se disponibile, altrimenti fallback
      const positionOnWall = wallResult.positionOnWall !== null 
        ? wallResult.positionOnWall 
        : this.getPositionOnWall(wall, point);

      // Salva la posizione per placeWindow, così il piazzamento usa sempre
      // lo stesso valore calcolato qui (non un ricalcolo dal ground plane)
      this._lastPreviewPositionOnWall = positionOnWall;
      
      // Verifica se la posizione è valida
      const isValid = this.validatePosition(wall, positionOnWall, this.currentWindowWidth);
      
      // Aggiorna preview
      const worldPos = new THREE.Vector3().lerpVectors(
        wall.start, 
        wall.end, 
        positionOnWall
      );
      
      this.previewMesh.position.copy(worldPos);
      this.previewMesh.position.y = this.currentSillHeight + this.currentWindowHeight / 2;
      this.previewMesh.rotation.y = wall.getAngle();
      
      // Cambia colore in base alla validità
      this.previewMesh.material.color.setHex(isValid ? 0x00cc00 : 0xff0000);
      this.previewMesh.visible = true;

      // Mostra le distanze live (solo se sono disponibili le coordinate schermo)
      if (mouseEvent && mouseEvent.clientX !== undefined) {
        let flipped = false;
        if (camera) {
          const angle   = wall.getAngle();
          const nx = -Math.sin(angle);
          const nz =  Math.cos(angle);
          const wallCenter = wall.getCenter();
          const toCam = new THREE.Vector3(
            camera.position.x - wallCenter.x,
            0,
            camera.position.z - wallCenter.z
          );
          flipped = (toCam.x * nx + toCam.z * nz) < 0;
        }
        this.updateMeasurementLabel(wall, positionOnWall, this.currentWindowWidth,
          mouseEvent.clientX, mouseEvent.clientY, isValid, flipped);
      }
    } else {
      this.previewMesh.visible = false;
      this.selectedWall = null;
      this._lastPreviewPositionOnWall = null;
      this.hideMeasurementLabel();
    }
  }
  
  placeWindow(point) {
    if (!this.placementMode || !this.selectedWall) return null;
    
    const wall = this.selectedWall;

    // Usa la posizione già calcolata dal raycast durante l'updatePreview;
    // se per qualche motivo non fosse disponibile, ricalcola dal punto ground
    const positionOnWall = this._lastPreviewPositionOnWall !== null
      ? this._lastPreviewPositionOnWall
      : this.getPositionOnWall(wall, point);
    
    // Validazione
    if (!this.validatePosition(wall, positionOnWall, this.currentWindowWidth)) {
      console.warn('Posizione non valida per la finestra');
      return null;
    }
    
    const window = new Window(
      wall,
      positionOnWall,
      this.currentWindowWidth,
      this.currentWindowHeight,
      this.currentSillHeight
    );
    this.addWindow(window);
    console.log(`Finestra posizionata (${this.currentWindowWidth}×${this.currentWindowHeight}, sill=${this.currentSillHeight}):`, window);
    return window;
  }
  
  addWindow(window) {
    // Aggiunge finestra alla parete
    if (!window.wall.windows) {
      window.wall.windows = [];
    }
    window.wall.windows.push(window);
    
    // Crea mesh e aggiungi alla scena
    const mesh = window.createMesh();
    this.scene.add(mesh);
    
    // Aggiunge alla lista
    this.windows.push(window);
    
    // Ricostruisce la parete con l'apertura
    window.wall.rebuildWallSegments();
    
    this.onOpeningsChange?.();
    return window;
  }
  
  removeWindow(windowId) {
    const index = this.windows.findIndex(w => w.id === windowId);
    if (index === -1) return false;
    
    const window = this.windows[index];
    const wall = window.wall;
    
    // Rimuove dalla parete
    if (wall.windows) {
      const wallIndex = wall.windows.findIndex(w => w.id === windowId);
      if (wallIndex !== -1) {
        wall.windows.splice(wallIndex, 1);
      }
    }
    
    // Rimuove dalla scena
    this.scene.remove(window.mesh);
    window.dispose();
    
    // Rimuove dalla lista
    this.windows.splice(index, 1);
    
    // Ricostruisce la parete
    wall.rebuildWallSegments();
    
    this.onOpeningsChange?.();
    return true;
  }
  
  findNearestWallWithRaycast(point, mouseEvent, camera) {
    const walls = this.wallController.getAllWalls();
    if (walls.length === 0) return { wall: null, positionOnWall: null };
    
    // Se ho mouseEvent e camera, usa il raycast diretto sulle pareti
    if (mouseEvent && camera) {
      const wallMeshes = [];
      const wallMap = new Map();
      
      walls.forEach(wall => {
        if (wall.mesh) {
          wall.mesh.traverse(child => {
            if (child.isMesh) {
              wallMeshes.push(child);
              wallMap.set(child, wall);
            }
          });
        }
      });
      
      if (wallMeshes.length > 0) {
        const rect = mouseEvent.target?.getBoundingClientRect?.() ??
                     { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
        const mouse = new THREE.Vector2(
          ((mouseEvent.clientX - rect.left) / rect.width) * 2 - 1,
          -((mouseEvent.clientY - rect.top) / rect.height) * 2 + 1
        );
        
        this.raycaster.setFromCamera(mouse, camera);
        const intersects = this.raycaster.intersectObjects(wallMeshes, false);
        
        if (intersects.length > 0) {
          const intersectedMesh = intersects[0].object;
          const wall = wallMap.get(intersectedMesh);
          
          if (wall) {
            const intersectionPoint = intersects[0].point;
            const positionOnWall = this.getPositionOnWall(wall, intersectionPoint);
            
            return { wall, positionOnWall };
          }
        }
      }
    }
    
    // Fallback: usa il metodo classico basato sulla distanza dal ground plane
    let nearestWall = null;
    let minDistance = Infinity;
    const maxDistance = 1.5;
    
    walls.forEach(wall => {
      const distance = this.distanceToWall(point, wall);
      if (distance < minDistance && distance < maxDistance) {
        minDistance = distance;
        nearestWall = wall;
      }
    });
    
    return { wall: nearestWall, positionOnWall: null };
  }
  
  findNearestWall(point) {
    const walls = this.wallController.getAllWalls();
    if (walls.length === 0) return null;
    
    let nearestWall = null;
    let minDistance = Infinity;
    const maxDistance = 1.5;
    
    walls.forEach(wall => {
      const distance = this.distanceToWall(point, wall);
      if (distance < minDistance && distance < maxDistance) {
        minDistance = distance;
        nearestWall = wall;
      }
    });
    
    return nearestWall;
  }
  
  distanceToWall(point, wall) {
    // Distanza punto-segmento
    const start = wall.start.clone();
    const end = wall.end.clone();
    const p = new THREE.Vector3(point.x, 0, point.z);
    
    const lineVec = new THREE.Vector3().subVectors(end, start);
    const lineLen = lineVec.length();
    lineVec.normalize();
    
    const pointVec = new THREE.Vector3().subVectors(p, start);
    const t = pointVec.dot(lineVec);
    
    let closestPoint;
    if (t <= 0) {
      closestPoint = start;
    } else if (t >= lineLen) {
      closestPoint = end;
    } else {
      closestPoint = start.clone().add(lineVec.multiplyScalar(t));
    }
    
    return p.distanceTo(closestPoint);
  }
  
  getPositionOnWall(wall, point) {
    // Calcola posizione normalizzata (0-1) sulla parete
    const start = wall.start.clone();
    const end = wall.end.clone();
    const p = new THREE.Vector3(point.x, 0, point.z);
    
    const lineVec = new THREE.Vector3().subVectors(end, start);
    const lineLen = lineVec.length();
    lineVec.normalize();
    
    const pointVec = new THREE.Vector3().subVectors(p, start);
    const t = pointVec.dot(lineVec) / lineLen;
    
    return Math.max(0, Math.min(1, t));
  }
  
  validatePosition(wall, positionOnWall, windowWidth) {
    // Verifica che la finestra non sia troppo vicina agli estremi
    const margin = windowWidth / 2 / wall.getLength();
    
    if (positionOnWall < margin || positionOnWall > 1 - margin) {
      return false;
    }
    
    // Calcola range della nuova finestra (in coordinate normalizzate 0-1)
    const newWindowStart = positionOnWall - windowWidth / 2 / wall.getLength();
    const newWindowEnd = positionOnWall + windowWidth / 2 / wall.getLength();
    
    // Converte margine in coordinate normalizzate
    const spacingNormalized = this.minimumSpacing / wall.getLength();
    
    // Verifica sovrapposizione con porte
    if (wall.doors) {
      for (const door of wall.doors) {
        const doorBox = door.getBoundingBox();
        
        // Verifica sovrapposizione con margine
        if (!(newWindowEnd + spacingNormalized < doorBox.start || 
              newWindowStart - spacingNormalized > doorBox.end)) {
          return false;
        }
      }
    }
    
    // Verifica sovrapposizione con altre finestre
    if (wall.windows) {
      for (const existingWindow of wall.windows) {
        const windowBox = existingWindow.getBoundingBox();
        
        // Verifica sovrapposizione CON MARGINE di 10cm
        if (!(newWindowEnd + spacingNormalized < windowBox.start || 
              newWindowStart - spacingNormalized > windowBox.end)) {
          return false;
        }
      }
    }
    
    return true;
  }
  
  getAllWindows() {
    return this.windows;
  }
  
  getWindowById(id) {
    return this.windows.find(w => w.id === id);
  }
  
  clearWindows() {
    this.windows.forEach(win => {
      this.scene.remove(win.mesh);
      win.dispose();
    });
    this.windows = [];
  }
  
  dispose() {
    this.windows.forEach(window => {
      this.scene.remove(window.mesh);
      window.dispose();
    });
    this.windows = [];
    this.removePreview();
    if (this.measurementLabel && this.measurementLabel.parentNode) {
      this.measurementLabel.parentNode.removeChild(this.measurementLabel);
      this.measurementLabel = null;
    }
  }
}

export default WindowController;